import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { AuthService } from "../../../../auth/auth.service";
import { SavedDataService } from "../../../../views/oes/services/saved-data.service";
import { Subscription } from "rxjs/Subscription";
import { ConfirmationService, MenuItem } from "primeng/api";
import { Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { ToastrService } from "ngx-toastr";
import { MPartSelectionModalComponent } from "../../components/part-selection-modal/m-part-selection-modal.component";
import { MatDialog } from "@angular/material";
import { MQuickOrderLineEditComponent } from "./quick-order-line-edit/m-quick-order-line-edit.component";

@Component({
  selector: "app-m-quick-order",
  templateUrl: "./m-quick-order.component.html",
  styleUrls: ["./m-quick-order.component.scss"],
  providers: [AuthService, SavedDataService],
})
export class MQuickOrderComponent implements OnInit, OnDestroy {
  private static charCodeTab = 9;
  private static charCodeNewLine = 10;
  private static charCodeComma = 44;

  public isWorkspaceSelected: boolean;
  public loading: boolean;
  public mode: string;
  public screenTitle: string;
  public permitAutoOrder: boolean;

  public itemsSV: Array<any>;
  public itemsIV: Array<any>;
  public selectedValid: any;
  public selectedInvalid: any;

  public poNumber: string;
  public jobNumber: string;
  public totalValue: number;

  @ViewChild("searchText") searchText: ElementRef;
  public searchBox: string;
  public searchBoxDisable: boolean;

  public contextMenuValid: Array<MenuItem>;
  public contextMenuInvalid: Array<MenuItem>;

  private subscription: Subscription;
  public savedDataServiceRef = SavedDataService;

  public quoteCheckbox = false;

  constructor(
    private authService: AuthService,
    private savedDataService: SavedDataService,
    private router: Router,
    private dialog: MatDialog,
    private title: Title,
    private confirmationService: ConfirmationService,
    private toast: ToastrService
  ) {}

  ngOnInit() {
    this.isWorkspaceSelected = this.getSelectedWorkspace();

    this.initValues();

    this.parseUrl(this.router.url);
    this.title.setTitle("OES - Quick Order");

    if (this.isWorkspaceSelected) {
      this.determineAutoOrderPermission();
      this.getData();
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onAutoOrder() {
    if (
      this.permitAutoOrder &&
      this.mode === this.savedDataServiceRef.MODE_SV1
    ) {
      const arr = [];
      arr.shift();
      this.loading = true;
      this.savedDataService.autoOrder(this.poNumber, this.jobNumber).subscribe(
        (result) => {
          if (result.success) {
            if (Number(result.linesAdded) > 0) {
              this.toast.success(
                "Added " + Number(result.linesAdded) + " lines via Auto Order."
              );
              this.getData();
            } else {
              this.loading = false;
              this.toast.warning("No eligible lines via Auto Order.", "", {
                timeOut: 5000,
              });
            }
          } else {
            this.loading = false;
            this.toast.warning(
              "Unexpected error encountered in recording auto order items."
            );
          }
        },
        () => {
          this.loading = false;
          this.toast.error("Request timed out.", "Service Error");
        }
      );
    }
  }

  onOpenContextMenuValid(event, cmValid, rowData) {
    this.selectedValid = rowData;

    event.preventDefault();
    event.stopPropagation();

    cmValid.show(event);
    return false;
  }

  onOpenContextMenuInvalid(event, cmInvalid, rowData) {
    this.selectedInvalid = rowData;

    event.preventDefault();
    event.stopPropagation();

    cmInvalid.show(event);
    return false;
  }

  onReload() {
    // reload records, do not clear PO Number & Job Number
    this.getData();
  }

  onSearch() {
    // search for matches based on search text
    // if list of matches is > 1, load part selector modal
    // if list of matches is = 1, part added to saved data
    this.searchBoxDisable = true;
    let startIndex = 0;
    const items = [];
    for (let i = 0; i < this.searchBox.length; i++) {
      const charCode = this.searchBox.charCodeAt(i);
      if (
        Number(charCode) === MQuickOrderComponent.charCodeNewLine ||
        Number(charCode) === MQuickOrderComponent.charCodeTab ||
        Number(charCode) === MQuickOrderComponent.charCodeComma
      ) {
        if (i > startIndex) {
          const searchText = this.searchBox.substring(startIndex, i);
          if (searchText.trim().length > 0) {
            items.push({
              searchText: searchText.trim(),
              PO_NUMBER: this.poNumber,
              JOB_NUMBER: this.jobNumber,
            });
          }
        }
        startIndex = i + 1;
      } else if (i === this.searchBox.length - 1) {
        const searchText = this.searchBox.substring(startIndex, i + 1);
        if (searchText.trim().length > 0) {
          items.push({
            searchText: searchText.trim(),
            PO_NUMBER: this.poNumber,
            JOB_NUMBER: this.jobNumber,
          });
        }
      }
    }

    if (items.length > 0) {
      this.loading = true;
      if (items.length === 1) {
        this.showMatches(items[0]);
      } else {
        this.searchBox = "";
        this.createSavedData(items, true);
      }
    } else {
      this.searchBox = "";
      this.searchBoxDisable = false;
      setTimeout(() => {
        this.searchText.nativeElement.focus();
      }, 200);
    }
  }

  onSubmit() {
    // submit saved data records
    if (
      this.mode === this.savedDataServiceRef.MODE_SV1 &&
      this.itemsSV.length > 0
    ) {
      this.loading = true;
      const MD1_IDs = this.itemsSV.map((savedData) => savedData["MD1_ID"]);
      this.savedDataService.checkRecentOrders(MD1_IDs).subscribe(
        (result) => {
          if (
            !result.hasOwnProperty("data") ||
            !result.hasOwnProperty("recentWindow")
          ) {
            this.loading = false;
            return;
          }

          const matchedMD1_IDs: Array<any> = result.data.map((MD1_ID) =>
            Number(MD1_ID)
          );
          if (matchedMD1_IDs.length > 0) {
            let message =
              "The following parts have been ordered recently (within the last " +
              result.recentWindow +
              "). " +
              "Are you sure you want to continue and place this order? <b>You may click NO to go back and edit the order</b>." +
              "<br><br>" +
              "<ol>";
            let matchCount = 1;
            let extraCount = 0;
            for (const row of this.itemsSV) {
              if (matchedMD1_IDs.indexOf(Number(row["MD1_ID"])) > -1) {
                if (matchCount > 4) {
                  extraCount++;
                } else {
                  message +=
                    "<li>" +
                    "Contract Bin ID: " +
                    row["CONTRACT_BIN_ID"] +
                    ", Item ID: " +
                    row["ITEM_ID"] +
                    ", CPN: " +
                    row["CUSTOMER_PART_NO"] +
                    "</li>";
                  matchCount++;
                }
              }
            }
            if (extraCount > 0) {
              message +=
                "</ol>" + "... and " + extraCount.toString() + " more lines.";
            }

            this.loading = false;
            this.confirmationService.confirm({
              header: "Warning",
              message: message,
              acceptLabel: "Yes, Submit Order",
              accept: () => {
                this.submitSavedData();
              },
            });
          } else {
            this.submitSavedData();
          }
        },
        () => {
          this.toast.error("Request timed out. Unexpected server error.");
        }
      );
    } else {
      this.submitSavedData();
    }
  }

  onValidate() {
    // validate invalid scans
    if (this.itemsIV.length > 0) {
      this.loading = true;
      const data = this.itemsIV.map((item) => {
        return {
          searchText: item.CONTRACT_BIN_ID,
          ID: item.ID,
          PO_NUMBER: this.poNumber,
          JOB_NUMBER: this.jobNumber,
        };
      });
      this.createSavedData(data, true, true);
    }
  }

  private computeTotalValue() {
    this.totalValue = 0;
    this.itemsSV.forEach((row) => {
      this.totalValue += Number(row.TOTAL_PRICE);
    });
  }

  private determineAutoOrderPermission() {
    this.savedDataService.preload().subscribe((preloadData) => {
      if (
        preloadData.hasOwnProperty("masterCustomer") &&
        preloadData.masterCustomer.hasOwnProperty(
          "CU0_ALLOW_INVENTORY_MANAGEMENT"
        )
      ) {
        this.permitAutoOrder =
          Number(preloadData.masterCustomer.CU0_ALLOW_INVENTORY_MANAGEMENT) ===
          1;
      }
    });
  }

  private getData() {
    this.loading = true;
    this.selectedValid = null;
    this.selectedInvalid = null;
    this.savedDataService.getItems(this.mode).subscribe(
      (result) => {
        this.itemsSV = result.valid.data;
        this.itemsIV = result.invalid.data;
        this.computeTotalValue();

        this.loading = false;
        this.searchBoxDisable = false;
        setTimeout(() => {
          this.searchText.nativeElement.focus();
        }, 200);
      },
      () => {
        this.toast.error("Request timed out.", "Service Error");

        this.loading = false;
        this.searchBoxDisable = false;
        setTimeout(() => {
          this.searchText.nativeElement.focus();
        }, 200);
      }
    );
  }

  private getSelectedWorkspace() {
    return this.authService.workspaceMasterCustomerID !== null;
  }

  private initValues() {
    this.loading = false;
    this.permitAutoOrder = false;

    this.poNumber = "";
    this.jobNumber = "";
    this.mode = "";
    this.screenTitle = "Replenishment";

    this.totalValue = 0;

    this.itemsSV = [];
    this.itemsIV = [];
    this.selectedValid = null;
    this.selectedInvalid = null;

    this.contextMenuValid = [
      {
        label: "Edit",
        icon: "pi pi-fw pi-pencil",
        command: () => this.onEditValid(),
      },
      {
        label: "Delete",
        icon: "pi pi-fw pi-trash",
        command: () => this.onDeleteValid(),
      },
    ];
    this.contextMenuInvalid = [
      {
        label: "Edit",
        icon: "pi pi-fw pi-pencil",
        command: () => this.onEditInvalid(),
      },
      {
        label: "Delete",
        icon: "pi pi-fw pi-trash",
        command: () => this.onDeleteInvalid(),
      },
    ];
  }

  private onEditValid() {
    if (this.selectedValid) {
      this.onEdit(this.selectedValid, true);
    }
  }

  private onEditInvalid() {
    if (this.selectedInvalid) {
      this.onEdit(this.selectedInvalid, false);
    }
  }

  private onDeleteValid() {
    if (this.selectedValid) {
      this.onDelete(this.selectedValid);
    }
  }

  private onDeleteInvalid() {
    if (this.selectedInvalid) {
      this.onDelete(this.selectedInvalid);
    }
  }

  private onEdit(record: any, valid: boolean) {
    // listen to onUpdate and update values and reload list (getData)
    const editDialog = this.dialog.open(MQuickOrderLineEditComponent, {
      width: "90%",
      maxWidth: window.innerWidth - 40 + "px",
      role: "dialog",
      hasBackdrop: true,
      disableClose: true,
      data: {
        scan: record,
        invalidScan: !valid,
      },
    });

    const editSubscription = editDialog.componentInstance.onUpdate.subscribe(
      (editedRecord) => {
        this.savedDataService.update(editedRecord, this.mode, !valid).subscribe(
          (result) => {
            if (result.success) {
              this.getData();
            } else {
              this.toast.error("Failed to update record.", "Error");
              this.loading = false;
            }
          },
          () => {
            this.toast.error("Request timed out.", "Service Error", {
              timeOut: 3000,
              tapToDismiss: true,
            });
            this.getData();
          }
        );

        editSubscription.unsubscribe();
      }
    );
  }

  private onDelete(record: any) {
    // delete selected record
    // reload list (getData)
    this.confirmationService.confirm({
      header: "Delete Confirmation",
      message: "Are you sure to delete selected record?",
      accept: () => {
        this.loading = true;
        this.savedDataService.delete([record["ID"]], this.mode).subscribe(
          (result) => {
            this.toast.success("Order updated", "Success");
            this.getData();
          },
          () => {
            this.toast.error("Request timed out.", "Service Error");
            this.getData();
          }
        );
      },
    });
  }

  private parseUrl(url: string) {
    if (url === "/mobile/oes/" + this.savedDataServiceRef.SV2_URL) {
      this.screenTitle = this.savedDataServiceRef.SV2_TITLE;
      this.mode = this.savedDataServiceRef.MODE_SV2;
    } else if (url === "/mobile/oes/" + this.savedDataServiceRef.SV3_URL) {
      this.screenTitle = this.savedDataServiceRef.SV3_TITLE;
      this.mode = this.savedDataServiceRef.MODE_SV3;
    } else {
      this.screenTitle = this.savedDataServiceRef.SV1_TITLE;
      this.mode = this.savedDataServiceRef.MODE_SV1;
    }
  }

  private submitSavedData() {
    if (this.itemsSV.length > 0) {
      this.loading = false;
      const IDs = this.itemsSV.map((savedData) => savedData.ID);
      this.savedDataService
        .submit(IDs, this.mode, this.quoteCheckbox)
        .subscribe(
          (result) => {
            if (result.success) {
              this.toast.success(
                "Submitted " + result.successCount + " records!",
                "Success"
              );
              this.getData();
            } else {
              if (Number(result.successCount) > 0) {
                this.toast.info(
                  "Submitted " + result.successCount + " records.",
                  "Status"
                );
                this.getData();
              } else {
                this.toast.error(
                  "Unable to submit the records. Please try later.",
                  "Error"
                );
                this.loading = false;
              }
            }
          },
          () => {
            this.toast.error("Request timed out.", "Service Error");
            this.loading = false;
          }
        );
    } else {
      this.toast.warning("You cannot submit an empty order.", "Notice");
    }
  }

  private showMatches(searchItem) {
    this.loading = true;
    this.savedDataService.showMatches(searchItem, this.mode).subscribe(
      (result) => {
        if (result.success) {
          if (result.data.length > 1) {
            // show result.data items
            this.searchBox = this.searchBox.trim();
            this.searchBoxDisable = false;
            this.loading = false;

            const selectionDialog = this.dialog.open(
              MPartSelectionModalComponent,
              {
                width: "90%",
                maxWidth: window.innerWidth - 40 + "px",
                role: "dialog",
                hasBackdrop: true,
                disableClose: true,
                data: {
                  parts: result.data,
                },
              }
            );

            const selectionSubscription =
              selectionDialog.componentInstance.onSelect.subscribe(
                (selection) => {
                  const data = [
                    {
                      MD1_ID: selection["MD1_ID"],
                      PO_NUMBER: this.poNumber,
                      JOB_NUMBER: this.jobNumber,
                    },
                  ];
                  this.searchBoxDisable = true;
                  this.searchBox = "";
                  this.createSavedData(data);

                  selectionSubscription.unsubscribe();
                }
              );
          } else {
            this.printCreationResultToast(result.creationResult);
            this.searchBox = "";
            this.getData();
            this.searchBoxDisable = false;
          }
        } else {
          this.searchBoxDisable = false;
          this.toast.error("Error encountered in recording scans.", "Error");
        }
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.searchBoxDisable = false;
      }
    );
  }

  private createSavedData(data, validate = false, validateFromInvalid = false) {
    this.savedDataService.create(data, this.mode, validate).subscribe(
      (result) => {
        if (result.success) {
          if (validateFromInvalid) {
            if (
              result.creationResult.hasOwnProperty("valid") &&
              Number(result.creationResult["valid"]) > 0
            ) {
              this.printCreationResultToast(result.creationResult);
            } else {
              this.toast.error(
                "Unable to validate any Invalid Scans.",
                "Error"
              );
            }
          } else {
            this.printCreationResultToast(result.creationResult);
          }
        } else {
          this.toast.error("Error encountered in recording scans.", "Error");
        }
        this.getData();
      },
      () => {
        this.toast.error("Request timed out.", "Service Error");
        this.loading = false;
        this.searchBoxDisable = false;
        setTimeout(() => {
          this.searchText.nativeElement.focus();
        }, 200);
      }
    );
  }

  private getLoadedLinesSummaryString(valid: number, invalid: number): string {
    const lineCount = valid + invalid;
    let message = "";
    if (lineCount > 0) {
      message += lineCount + " line" + (lineCount > 1 ? "s" : "");
      message +=
        " (" +
        (valid > 0 ? valid + " valid" : "") +
        (valid > 0 && invalid > 0 ? ", " : "") +
        (invalid > 0 ? invalid + " invalid" : "") +
        ")";
    } else {
      message += "0 lines";
    }
    return message;
  }

  private getDuplicateScansErrorString(duplicate: number): string {
    let message = "";
    if (duplicate > 0) {
      message +=
        "Duplicate barcode scanning is disabled (" +
        duplicate +
        " duplicates added to Invalid scans).";
    }
    return message;
  }

  private printCreationResultToast(creationResult: any) {
    this.toast.info(
      this.getLoadedLinesSummaryString(
        creationResult.hasOwnProperty("valid")
          ? Number(creationResult["valid"])
          : 0,
        creationResult.hasOwnProperty("invalid")
          ? Number(creationResult["invalid"])
          : 0
      ) +
        " recorded. " +
        this.getDuplicateScansErrorString(
          creationResult.hasOwnProperty("duplicate")
            ? Number(creationResult["duplicate"])
            : 0
        ),
      "Update",
      {
        timeOut: 5000,
      }
    );
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/modules/mobile/views/quick-order/m-quick-order.component.ts